import React, { useEffect, useMemo, useRef } from 'react';
import { useTypedSelector } from '@/hooks/useTypedSelector';
import { groupConversationsByDate } from '@/utils/chatHistoryGrouping';
import Skeleton from '@srm/ui/skeleton';
import { ConversationGroupHeader } from './ConversationGroupHeader';
import { ConversationListItem } from './ConversationListItem';

interface IProps {
    onSelect: (conversationId: string) => void;
    onStartRename: (conversationId: string) => void;
    onCommitRename: (conversationId: string, newTitle: string) => void;
    onCancelRename: () => void;
    onDelete: (conversationId: string) => void;
    onLoadMore: () => void;
    renamingId: string | null;
}

const SKELETON_COUNT = 4;

export const ConversationList: React.FC<IProps> = ({
    onSelect,
    onStartRename,
    onCommitRename,
    onCancelRename,
    onDelete,
    onLoadMore,
    renamingId,
}) => {
    const items = useTypedSelector((s) => s.chatAIHistory.items);
    const total = useTypedSelector((s) => s.chatAIHistory.listTotal);
    const loading = useTypedSelector((s) => s.chatAIHistory.listLoading);
    const activeId = useTypedSelector((s) => s.chatAIHistory.currentConversationId);

    const groups = useMemo(() => groupConversationsByDate(items), [items]);
    const hasMore = items.length < total;
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const node = scrollRef.current;
        if (!node) return;
        const handler = () => {
            if (loading !== 'idle' || !hasMore) return;
            const remaining = node.scrollHeight - node.scrollTop - node.clientHeight;
            if (remaining < 80) onLoadMore();
        };
        node.addEventListener('scroll', handler);
        return () => node.removeEventListener('scroll', handler);
    }, [loading, hasMore, onLoadMore]);

    if (loading === 'reloading' && items.length === 0) {
        return (
            <div className="chat-ai-sidebar__list" ref={scrollRef}>
                {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                    <Skeleton key={i} height={36} radius="sm" mb={6} />
                ))}
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <div className="chat-ai-sidebar__list" ref={scrollRef}>
                <div className="chat-ai-sidebar__empty">Здесь будут ваши чаты</div>
            </div>
        );
    }

    return (
        <div className="chat-ai-sidebar__list" ref={scrollRef}>
            {groups.map((group) => (
                <div key={group.key} className="chat-ai-sidebar__group">
                    <ConversationGroupHeader label={group.label} />
                    {group.items.map((item) => (
                        <ConversationListItem
                            key={item.conversationId}
                            item={item}
                            isActive={item.conversationId === activeId}
                            isRenaming={item.conversationId === renamingId}
                            onSelect={onSelect}
                            onStartRename={onStartRename}
                            onCommitRename={onCommitRename}
                            onCancelRename={onCancelRename}
                            onDelete={onDelete}
                        />
                    ))}
                </div>
            ))}
            {loading === 'pending' && hasMore && (
                <div className="chat-ai-sidebar__loading-more">
                    <Skeleton height={36} radius="sm" mb={6} />
                </div>
            )}
        </div>
    );
};
